import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  CheckRounded,
  EventOutlined,
  GroupsOutlined,
  ListAltOutlined,
  LogoutOutlined,
  RemoveRounded,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PanelActionFooter } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designType } from "services/constants/designTokens";
import { pfExitCodeHeader } from "pages/PayRoll/PayRollHeaders";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { ToneChip } from "pages/PayRoll/Salary/Shared/salaryUi";
import { ChipMultiSelect, RecordHeaderCard, StatCardGrid, StatutoryList } from "pages/PayRoll/Shared/statutoryUi";
import { saveExitMapping } from "./pfCodesApi";

// Exit Codes tab (P3) and the Edit Exit Mapping drawer (P3a). The EPFO code
// list (C, S, R, D, P) is maintained by Cauver and read-only; the company
// maps its own exit reasons to it. Each reason maps to one code only. Codes,
// reasons and the mapping come from the PF API (index.js loads them); a save
// returns the whole tab data.

const ALL = "all";
const MAPPING_COLORS = { Mapped: "#12B76A", "Not mapped": "#DC2626" };
const { orange, pink, indigo } = detailAccentPalette;

const MappingPanel = ({ open, code, allReasons, domain, companyId, onClose, onSaved }) => {
  const [reasons, setReasons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  useEffect(() => {
    if (!open || !code) return;
    setReasons(code.reasons.map((reason) => reason.id));
    setSaveError("");
  }, [open, code]);
  if (!code) return null;

  const status = reasons.length ? "Mapped" : "Not mapped";
  // Null until PF exit records exist (backend phase 2).
  const recent = code.recent;
  // A reason already on another code is shown but can't be picked here.
  const options = allReasons.map((reason) => {
    const owner = reason.mapped_code && reason.mapped_code !== code.code ? reason.mapped_code : null;
    return {
      value: reason.id,
      label: reason.name,
      disabled: Boolean(owner),
      hint: owner ? `Mapped to code ${owner}` : undefined,
    };
  });

  const save = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const { message, ...data } = await saveExitMapping({ domain, companyId, code: code.code, reasonIds: reasons });
      showToast("success", message || `Exit mapping for code ${code.code} saved.`);
      onSaved(data);
    } catch (err) {
      setSaveError(err.message || "Couldn't save the mapping. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Edit Exit Mapping"
      subtitle={`EPFO code ${code.code} · ${code.label.replace(/\s*\(.*\)$/, "")}`}
      bodySx={panelBodySx}
      footer={<PanelActionFooter onCancel={onClose} primaryLabel="Save" primaryProps={{ onClick: save, loading: saving, disabled: saving }} />}
    >
      <Mui.Stack spacing={2}>
        {saveError ? <RequestInfoBanner tone="danger" title="Couldn't save the mapping." body={saveError} /> : null}
        <RecordHeaderCard
          avatar={code.code}
          title={code.label}
          chip={<RequestStatusChip status={status} colorCode={MAPPING_COLORS[status]} />}
          meta="EPFO code list item · name and code cannot be edited"
          tone={reasons.length ? "teal" : "danger"}
        />
        <RequestDetailsSection
          title="CODE RULES (SET BY EPFO, READ-ONLY)"
          fields={[
            {
              icon: EventOutlined,
              label: "Date of exit",
              value: "Required",
              helper: "Taken from the employee's exit date; must be after the date of joining",
              accent: orange,
            },
            {
              icon: LogoutOutlined,
              label: "Effect",
              value: "Member is exited on the EPFO portal",
              helper: "No ECR line from the month after exit; enables the employee's PF withdrawal / transfer",
              accent: pink,
              multiline: true,
            },
          ]}
        />
        <FormSection title="Mapped Cauver Reasons" info="From People › Exit reasons. Each Cauver reason can map to only one EPFO code.">
          <Field wide label="Exit reasons">
            <ChipMultiSelect value={reasons} onChange={setReasons} options={options} placeholder="+ Add" />
          </Field>
        </FormSection>
        <RequestDetailsSection
          title="USED RECENTLY"
          fields={[
            {
              icon: GroupsOutlined,
              label: recent?.month ? `${recent.month} PF exits report` : "PF exits report",
              value: recent?.employees?.length
                ? `${recent.employees.length} employees · ${recent.employees.join(", ")}`
                : "Not used in the last 3 months",
              accent: indigo,
              multiline: true,
            },
          ]}
        />
        <RequestInfoBanner body="Changes apply to exits reports generated from now on." />
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

export const PfExitCodesTab = ({ domain, companyId, data, onChange }) => {
  const { userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const { codes, reasons } = data;
  const [card, setCard] = useState(ALL);
  const [editCode, setEditCode] = useState(null);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "pf_exit_codes_saved_views",
    fullHeaders: pfExitCodeHeader,
  });

  const allRows = useMemo(
    () =>
      codes.map((code) => ({
        id: code.code,
        ...code,
        reasons_label: code.reasons.map((reason) => reason.name).join(", "),
        date_required: "Yes",
        date_used: "Exit date",
      })),
    [codes],
  );
  const isMapped = (row) => row.status === "Mapped";
  const rows = card === "Mapped" ? allRows.filter(isMapped) : card === "unused" ? allRows.filter((row) => !isMapped(row)) : allRows;

  const cards = [
    { key: ALL, title: "EPFO exit codes", value: allRows.length, icon: ListAltOutlined, tone: "blue" },
    // All three cards count EPFO codes, so they add up (all = mapped + not used).
    { key: "Mapped", title: "Mapped", value: allRows.filter(isMapped).length, icon: CheckRounded, tone: "green" },
    { key: "unused", title: "Not used", value: allRows.filter((row) => !isMapped(row)).length, icon: RemoveRounded, tone: "amber" },
  ];

  const renderCell = (row, key) => {
    switch (key) {
      case "code":
        return <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: 700 }}>{row.code}</Mui.Typography>;
      case "reasons_label":
        return row.reasons.length ? (
          <Mui.Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {row.reasons.map((reason) => (
              <ToneChip key={reason.id} label={reason.name} tone="blue" />
            ))}
          </Mui.Stack>
        ) : (
          <Mui.Typography sx={{ ...designType.tableBodyCell, color: designColors.slate500 }}>No reason mapped</Mui.Typography>
        );
      case "status":
        return <RequestStatusChip status={row.status} colorCode={MAPPING_COLORS[row.status]} />;
      default:
        return row[key] || "—";
    }
  };

  return (
    <>
      <StatCardGrid cards={cards} value={card} onChange={setCard} />
      <StatutoryList
        title="PF Exit Codes"
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={rows}
        renderCell={renderCell}
        onRowClick={(row) => setEditCode(row.code)}
        selectedKey={editCode}
        itemLabel="codes"
        searchPlaceholder="Search code or reason"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
      />
      <Mui.Stack spacing={1.5} sx={{ mt: 2 }}>
        <RequestInfoBanner body="EPFO code list maintained by Cauver · verify on the EPFO portal before release." />
      </Mui.Stack>

      <MappingPanel
        open={Boolean(editCode)}
        code={codes.find((code) => code.code === editCode) || null}
        allReasons={reasons}
        domain={domain}
        companyId={companyId}
        onClose={() => setEditCode(null)}
        onSaved={(next) => {
          onChange(next);
          setEditCode(null);
        }}
      />
    </>
  );
};
